import { ArrowRight, KeyRound } from "lucide-react";
import type { ReactNode } from "react";

import { Logo } from "@/components/brand";
import { EXPIRY_WARNING_DAYS } from "@/utils/password-expiry";
import { cn } from "@/utils/utils";

/**
 * The shell both password cards wear: the same brand navy, one slow float, one
 * sheen, so every screen in the flow reads as one place. Decorative, so the
 * whole thing is hidden from assistive technology, and the global
 * reduced-motion rule stops both animations. The chrome inside stays in
 * English, as the member card's does; the sentence a person actually has to
 * read is on the form beside it.
 */
function CardShell({ children }: { children: ReactNode }) {
  return (
    <div aria-hidden className="relative w-full max-w-xs sm:max-w-sm">
      {/* A second card behind: the credential on its way out. */}
      <div className="bg-brand-200/70 dark:bg-brand-600/40 absolute inset-x-4 -top-3 h-full rounded-2xl" />

      <div className="animate-float-card bg-brand-700 relative isolate flex aspect-[1.586] flex-col overflow-hidden rounded-2xl p-5 text-white shadow-[0_24px_48px_-16px_rgba(15,33,65,0.55)] sm:p-6">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(90%_120%_at_100%_110%,var(--color-brand-500)_0%,transparent_60%),radial-gradient(60%_80%_at_-10%_-10%,var(--color-brand-600)_0%,transparent_60%)]" />
        <div className="animate-sheen absolute inset-y-0 -left-1/4 w-1/3 bg-gradient-to-r from-transparent via-white/25 to-transparent" />

        <div className="flex items-start justify-between">
          <span className="grid size-11 place-items-center rounded-xl border border-white/15 bg-white/10 backdrop-blur-sm">
            <KeyRound className="size-5" />
          </span>
          <Logo tone="light" className="w-24 sm:w-28" />
        </div>

        {children}
      </div>
    </div>
  );
}

const Dots = ({ filled }: { filled: boolean }) => (
  <div className="mt-1.5 flex gap-1.5">
    {Array.from({ length: 6 }, (_, index) => (
      <span
        key={index}
        className={cn("size-2 rounded-full", filled ? "bg-white/35" : "border border-white/70")}
      />
    ))}
  </div>
);

/**
 * The set-password screen: a credential someone else issued, being handed over
 * for one only its owner knows.
 */
export function HandoverCard() {
  return (
    <CardShell>
      <div className="mt-auto flex items-end justify-between gap-3 pt-5">
        <div>
          <p className="text-footnote text-white/50">Issued to you</p>
          <Dots filled />
        </div>
        <ArrowRight className="mb-1.5 size-4 shrink-0 text-white/40" />
        <div>
          <p className="text-footnote text-white/70">Yours</p>
          <Dots filled={false} />
        </div>
      </div>
    </CardShell>
  );
}

/** The change-password screen: how long the current credential has left. */
export function CredentialCard({ days = null }: { days?: number | null }) {
  const countdown =
    days === null
      ? null
      : days < 0
        ? "Expired"
        : days === 0
          ? "Today"
          : days === 1
            ? "Tomorrow"
            : `${days} days`;
  // The bar empties across the warning window, so "soon" is visible before the
  // number is read. Red once past due, amber on the last day.
  const left = Math.max(0, Math.min(days ?? 0, EXPIRY_WARNING_DAYS));
  const tone = days === null || days > 1 ? "bg-success" : days < 0 ? "bg-danger" : "bg-warning";

  return (
    <CardShell>
      <div className="mt-auto pt-5">
        <p className="text-footnote text-white/60">
          {days !== null && days < 0 ? "Password" : "Expires"}
        </p>
        <p className="text-title-2 mt-0.5 font-semibold tracking-tight">{countdown ?? "—"}</p>

        <div className="mt-4 h-1 overflow-hidden rounded-full bg-white/20">
          <div
            className={cn("h-full rounded-full transition-[width]", tone)}
            style={{ width: `${days === null ? 66 : (left / EXPIRY_WARNING_DAYS) * 100}%` }}
          />
        </div>
      </div>
    </CardShell>
  );
}
